import React, { ButtonHTMLAttributes, ReactNode } from 'react';

import styles from './Sidebar.module.scss';
import {
  DesignSystemSidebarDensity,
  getSidebarItemClasses,
  getSidebarShellClasses,
} from './utils';
import { DesignSystemTypography } from '../../tokens/tokens';
import { Tooltip } from '../Hint/Tooltip';
import { Icon } from '../Icon/Icon';
import {
  DesignSystemIconName,
  DesignSystemIconSize,
  DesignSystemIconTone,
} from '../Icon/utils';
import { IconButton } from '../IconButton/IconButton';
import {
  DesignSystemIconButtonSize,
  DesignSystemIconButtonVariant,
} from '../IconButton/utils';
import { Typography } from '../Typography/Typography';

type SidebarShellProps = {
  children: ReactNode;
  'aria-label': string;
  density?: DesignSystemSidebarDensity;
  header?: ReactNode;
  headerActionLabel?: string;
  onHeaderClick?: () => void;
  closeLabel?: string;
  onClose?: () => void;
  footer?: ReactNode;
  className?: string;
};

type SidebarItemProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> & {
  icon: DesignSystemIconName;
  label: string;
  badge?: ReactNode;
  isActive?: boolean;
  isCollapsed?: boolean;
};

export const SidebarShell = ({
  children,
  density = DesignSystemSidebarDensity.COMPACT,
  header,
  headerActionLabel,
  onHeaderClick,
  closeLabel = 'Close sidebar',
  onClose,
  footer,
  className,
  ...props
}: SidebarShellProps): JSX.Element => {
  const shouldRenderHeader = Boolean(header || onClose);

  return (
    <aside
      className={getSidebarShellClasses({ density, className })}
      {...props}
    >
      {shouldRenderHeader && (
        <div className={styles.header}>
          <div className={styles.header__content}>
            {header ? (
              onHeaderClick ? (
                <button
                  type="button"
                  aria-label={headerActionLabel}
                  className={styles.header_action}
                  onClick={onHeaderClick}
                >
                  {header}
                </button>
              ) : (
                header
              )
            ) : null}
            {onClose ? (
              <IconButton
                aria-label={closeLabel}
                icon="navigation/left-panel/16"
                variant={DesignSystemIconButtonVariant.GHOST}
                size={DesignSystemIconButtonSize.SMALL}
                className={styles.close_button}
                onClick={onClose}
              />
            ) : null}
          </div>
        </div>
      )}
      <nav className={styles.navigation}>{children}</nav>
      {footer && <div className={styles.footer}>{footer}</div>}
    </aside>
  );
};

export const SidebarItem = ({
  icon,
  label,
  badge,
  isActive,
  isCollapsed,
  className,
  disabled,
  'aria-label': ariaLabel,
  ...props
}: SidebarItemProps): JSX.Element => {
  const button = (
    <button
      type="button"
      className={getSidebarItemClasses({
        isActive,
        isDisabled: disabled,
        isCollapsed,
        className,
      })}
      disabled={disabled}
      aria-current={isActive ? 'page' : undefined}
      aria-label={ariaLabel || (isCollapsed ? label : undefined)}
      {...props}
    >
      <span className={styles.item__icon}>
        <Icon
          name={icon}
          size={DesignSystemIconSize.LARGE}
          tone={
            disabled
              ? DesignSystemIconTone.DISABLED
              : DesignSystemIconTone.PRIMARY
          }
        />
      </span>
      <Typography
        variant={DesignSystemTypography.LABEL_2_REGULAR}
        tag="span"
        className={styles.item__label}
      >
        {label}
      </Typography>
      {badge && <span className={styles.item__badge}>{badge}</span>}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip text={label} side="right" sideOffset={-10} disabled={disabled}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export type { SidebarItemProps, SidebarShellProps };
